import type { ComponentType, SVGProps } from 'react';
import {
  NavDashboardIcon,
  NavMenuIcon,
  NavOrdersIcon,
  NavMobileDashboardIcon,
  NavMobileMenuIcon,
  NavMobileOrdersIcon,
} from '@noor/ui';

/**
 * The Eats nav sections — one definition per section, shared by both nav
 * surfaces. The shell maps over an ordered list and derives the active entry
 * from the current path. A future part of the super-app (Market, Pharmacy, …)
 * gets its own nav config + shell widget, reusing the same `TopNavShell`
 * primitives.
 */
export interface EatsNavItem {
  /** Absolute route path this segment links to. */
  path: string;
  /** i18n key under the `eats` namespace (`nav.<key>`). */
  labelKey: string;
  /**
   * i18n key for the MOBILE tab bar's label. Separate from `labelKey` because
   * the design labels the same sections differently there — "Главное" for the
   * dashboard, "Заведение" for the branches — shorter words that fit a fifth
   * of the screen and read as destinations rather than admin nouns.
   */
  tabLabelKey: string;
  /** Desktop top-nav glyph — inherits `currentColor` from tab state. */
  icon: ComponentType<SVGProps<SVGSVGElement>>;
  /**
   * MOBILE bottom-nav glyph. A separate mark from `icon`, not a re-render of
   * it at a smaller size: the Figma mobile tab bar (node 26264:8934) uses
   * different source instances for the same five concepts — a filled house
   * instead of a bar chart for "dashboard", a location pin instead of a
   * folder for "menu" — so the two designs disagree about what each icon
   * looks like, and the code keeps that disagreement rather than picking one
   * mark to represent both.
   */
  mobileIcon: ComponentType<SVGProps<SVGSVGElement>>;
}

/**
 * Desktop top-nav order (the `SegmentedNav`, which renders each `labelKey`):
 * Dashboard · Orders · Venues · Menu · Users — the left-to-right sequence of
 * the Figma desktop design (node 838:14149).
 *
 * The venues tab lands on the BRANCH LIST (`/eats/branches`): a merchant owns
 * several branches, so the list is the real entry point, and each row opens the
 * branch editor as a modal over it rather than navigating to its own screen.
 */
export const EATS_NAV: readonly EatsNavItem[] = [
  {
    path: '/eats',
    labelKey: 'nav.dashboard',
    tabLabelKey: 'nav.tabs.dashboard',
    icon: NavDashboardIcon,
    mobileIcon: NavMobileDashboardIcon,
  },
  {
    path: '/eats/orders',
    labelKey: 'nav.orders',
    tabLabelKey: 'nav.tabs.orders',
    icon: NavOrdersIcon,
    mobileIcon: NavMobileOrdersIcon,
  },
  {
    path: '/eats/menu',
    labelKey: 'nav.menu',
    tabLabelKey: 'nav.tabs.menu',
    icon: NavMenuIcon,
    mobileIcon: NavMobileMenuIcon,
  },
] as const;

/**
 * Mobile bottom-bar order (the `BottomNav`, which renders each `mobileIcon`):
 * Dashboard · Menu · Orders. Derived from `EATS_NAV` so each section stays
 * defined once.
 */
const EATS_BOTTOM_NAV_ORDER = ['/eats', '/eats/menu', '/eats/orders'] as const;

export const EATS_BOTTOM_NAV: readonly EatsNavItem[] = EATS_BOTTOM_NAV_ORDER.map(
  (path) => {
    const item = EATS_NAV.find((navItem) => navItem.path === path);
    if (!item) throw new Error(`EATS_BOTTOM_NAV: no EATS_NAV entry for "${path}"`);
    return item;
  },
);
